import axios from "axios";
import { Download, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, apiError, authHeaders, formatDateIt } from "@/lib/api";

const ALL = "all";

export const BookingsTable = ({ data, filter, onFilter, onChanged, readOnly = false, title = "Prenotazioni" }) => {
  const exportCsv = async () => {
    try {
      const params = filter !== ALL ? { eventDate: filter } : {};
      const res = await axios.get(`${API}/admin/bookings/export`, { params, headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prenotazioni-${filter !== ALL ? filter : "tutte"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Eliminare la prenotazione di ${b.nome} ${b.cognome}?`)) return;
    try {
      await axios.delete(`${API}/admin/bookings/${b.id}`, { headers: authHeaders() });
      toast.success("Prenotazione eliminata");
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resend = async (b) => {
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/resend`, {}, { headers: authHeaders() });
      toast.success("Inviata al Google Sheet");
    } catch (err) {
      toast.error(apiError(err));
    }
    onChanged();
  };

  return (
    <section className="glass-strong rounded-[1.5rem] p-6 md:p-8 flex flex-col gap-5 min-w-0" data-testid="admin-bookings-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading italic text-white text-3xl tracking-[-1px]" data-testid="admin-bookings-title">{title}</h2>
          <p className="text-sm text-white/50 mt-1" data-testid="admin-bookings-count">{data.items.length} prenotazioni</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={onFilter}>
            <SelectTrigger data-testid="admin-event-filter" className="glass rounded-full border-0 h-10 px-4 w-44 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black border-white/10 text-white rounded-2xl">
              <SelectItem value={ALL} className="rounded-xl focus:bg-white/10 focus:text-white">Tutti gli eventi</SelectItem>
              {data.eventDates.map((d) => (
                <SelectItem key={d} value={d} className="rounded-xl focus:bg-white/10 focus:text-white">{formatDateIt(d).long} {d.slice(0, 4)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={exportCsv} data-testid="admin-export-csv" className="bg-white text-black rounded-full px-4 h-10 text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto -mx-2">
        <Table data-testid="admin-bookings-table">
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              {["Nome", "Telefono", "Anno", "Lista", "Marketing", "Evento", "Ricevuta", ...(readOnly ? [] : ["Sheet", ""])].map((h) => (
                <TableHead key={h} className="text-white/50 text-xs uppercase tracking-[0.1em]">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow className="border-white/10"><TableCell colSpan={9} className="text-center text-white/40 py-10">Nessuna prenotazione</TableCell></TableRow>
            )}
            {data.items.map((b) => (
              <TableRow key={b.id} className="border-white/10 hover:bg-white/[0.03]" data-testid={`booking-row-${b.id}`}>
                <TableCell className="text-white whitespace-nowrap">{b.nome} {b.cognome}</TableCell>
                <TableCell className="whitespace-nowrap"><a href={`tel:${b.telefono}`} className="text-white/80 hover:text-white">{b.telefono}</a></TableCell>
                <TableCell className="text-white/70">{b.anno}</TableCell>
                <TableCell className="text-white/70 whitespace-nowrap">{b.lista}</TableCell>
                <TableCell className="text-white/70">{b.marketingConsent ? "Sì" : "No"}</TableCell>
                <TableCell className="text-white/70 whitespace-nowrap">{formatDateIt(b.eventDate).short}</TableCell>
                <TableCell className="text-white/50 whitespace-nowrap text-xs">{new Date(b.createdAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                {!readOnly && (
                <TableCell className="whitespace-nowrap" data-testid={`sheet-status-${b.id}`}>
                  {b.sheetStatus === "ok" ? (
                    <span className="text-xs text-emerald-300/80">Inviata</span>
                  ) : (
                    <button onClick={() => resend(b)} data-testid={`resend-booking-${b.id}`} className="text-xs text-amber-300/80 hover:text-amber-200 flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> {b.sheetStatus === "error" ? "Errore, reinvia" : "Invia"}
                    </button>
                  )}
                </TableCell>
                )}
                {!readOnly && (
                <TableCell>
                  <button onClick={() => remove(b)} aria-label="Elimina" data-testid={`delete-booking-${b.id}`} className="p-2 rounded-full text-white/40 hover:text-red-300 hover:bg-white/5">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
};
